import {
  RUTERO_DECLARABLE_PASSENGER_FIELDS,
  parseRuteroPassengerFieldRef,
  ruteroPassengerFieldRef,
  sanitizeRuteroMissingPassengerFields,
  sanitizeRuteroRecommendedPassengerInfo,
  sanitizeRuteroRequiredPassengerInfo,
} from '@bandido/shared/types';
import type {
  RuteroDeclarablePassengerField,
  RuteroPassengerFieldRef,
  RuteroPassengerInfoScope,
} from '@bandido/shared/types';

import type { RuteroPassengerPayload } from './queries';
import { bookingContactPassenger, bookingContactPassengerIndex } from './bookingTravelerRoster';
import { isValidE164 } from './phoneNumber';

/**
 * Autoridad proactiva de datos de pasajero para el formulario de reserva.
 *
 * La base es SIEMPRE la oferta: cada una declara lo que su conector exige
 * (`requiredPassengerInfo`) y lo que aprovecha si viene
 * (`recommendedPassengerInfo`), con los nombres ya normalizados al contrato de
 * pasajero. Un rechazo limpio del hold puede promover refs sanitizados sobre
 * esa base mediante `withMissingPassengerFields`. Acá no hay ninguna regla por
 * vertical: las que había ("ferry pide email y nacionalidad", "rail pide
 * teléfono del titular") eran adivinadas nuestras, sin publicar, y por eso
 * transfers pedía de menos y ferry traducía nombres de proveedor a mano.
 *
 * Vale para los 9 verticales. Un vertical que no declara nada pide solo el
 * contrato base (nombre y apellido).
 */

/** Cómo se pinta cada campo. Exhaustivo a propósito: un campo nuevo del
 *  vocabulario no compila hasta que se decide con qué control se carga. */
export type PassengerFieldInput =
  | 'text'
  | 'email'
  | 'date'
  | 'country'
  | 'phone'
  | 'multiline'
  | 'none';

export const PASSENGER_FIELD_INPUT: Record<
  RuteroDeclarablePassengerField,
  PassengerFieldInput
> = {
  // El tipo ya se elige en la fila del pasajero y SIEMPRE viaja con un valor:
  // declararlo no agrega ningún control ni puede faltar nunca.
  type: 'none',
  title: 'text',
  birthDate: 'date',
  email: 'email',
  phone: 'phone',
  nationality: 'country',
  countryOfResidence: 'country',
  passportNumber: 'text',
  passportExpiry: 'date',
  flightNumber: 'text',
  returnFlightNumber: 'text',
  // Texto libre para el operador (una instrucción, no un dato): multilínea.
  driverNotes: 'multiline',
};

/**
 * Cuántas celdas de la grilla ocupa el control de un campo.
 *
 * El teléfono no es un campo: es un control COMPUESTO (selector de país más
 * número). En una celda del ancho de un campo simple, al número le quedan unos
 * 100 px, donde no entra un teléfono. Ocupa dos.
 *
 * Es una propiedad del campo y no un ancho a mano en el lugar donde se dibuja:
 * el teléfono aparece en la fila cuando la oferta lo pide de todos y en el
 * bloque del titular cuando lo pide solo de él, y las dos veces mide igual.
 * El cálculo del ancho de celda también lo cuenta doble — si no, una fila con
 * teléfono más tres campos se pasa de doce columnas y envuelve.
 *
 * El multilínea también ocupa dos: es una instrucción en prosa (hasta 500
 * caracteres), no un dato — en una celda de campo simple no se puede ni leer
 * lo que se escribió.
 */
export function passengerFieldCells(
  field: RuteroDeclarablePassengerField,
): 1 | 2 {
  const input = PASSENGER_FIELD_INPUT[field];
  return input === 'phone' || input === 'multiline' ? 2 : 1;
}

/** Las celdas que ocupa un conjunto de campos declarados. */
export function passengerFieldsCellCount(
  fields: readonly DeclaredPassengerField[],
): number {
  return fields.reduce((cells, entry) => cells + passengerFieldCells(entry.field), 0);
}

/**
 * Topes por campo, IGUALES a los del DTO de hold (`RuteroPassengerDto`).
 * Recortar en el input lo que el borde va a rechazar es preferible a un 400
 * después de cargar toda la reserva; que los dos números convivan sin coincidir
 * sería truncado silencioso (21.22.5).
 */
export const PASSENGER_FIELD_MAX_LENGTH: Record<
  RuteroDeclarablePassengerField,
  number | undefined
> = {
  type: undefined,
  title: 20,
  birthDate: undefined,
  email: 120,
  phone: 30,
  nationality: 2,
  countryOfResidence: 2,
  passportNumber: 20,
  passportExpiry: undefined,
  flightNumber: 12,
  returnFlightNumber: 12,
  // El tope del contrato del hub (`driverNotes.maxLength`). OJO: este campo
  // NO se recorta en el input — ver `passengerFieldValueTooLong`.
  driverNotes: 500,
};

/**
 * El NIVEL de un campo declarado: CUÁNDO lo valida el proveedor y, por eso,
 * cuándo lo valida el proveedor. Bandido puede exigirlo antes si esa es la
 * última frontera que transporta el dato.
 *
 * - `hold`: sin el dato el proveedor rechaza la reserva ⇒ bloquea Reservar.
 * - `quote`: una sesión elegida lo necesita para obtener el precio autoritativo.
 * - `confirm`: el proveedor lo valida al confirmar (hoy solo Hotelbeds
 *   transfers), pero bloquea Reservar porque el confirm no transporta pax.
 * - `recommended`: el operador lo aprovecha si viene (una edad real da mejor
 *   tarifa por banda) ⇒ no bloquea nada, nunca.
 */
export type PassengerFieldLevel = 'hold' | 'quote' | 'confirm' | 'recommended';

/**
 * Precedencia entre niveles. Un campo declarado dos veces (requerido y
 * recomendado, o por alcances distintos) se queda con la exigencia MÁS fuerte:
 * pedirlo de menos rompe el hold contra el proveedor.
 */
const LEVEL_RANK: Record<PassengerFieldLevel, number> = {
  recommended: 0,
  confirm: 1,
  quote: 2,
  hold: 3,
};

function strongest(
  current: PassengerFieldLevel | undefined,
  next: PassengerFieldLevel,
): PassengerFieldLevel {
  if (!current) return next;
  return LEVEL_RANK[next] > LEVEL_RANK[current] ? next : current;
}

export interface DeclaredPassengerField {
  field: RuteroDeclarablePassengerField;
  /** Cuándo lo valida el proveedor, y por eso qué decisión bloquea. */
  level: PassengerFieldLevel;
}

/**
 * Rail cotiza youth/senior con edad real, pero el hold no acepta `age`: exige
 * `birthDate` para que Rutero pueda comprobar la misma composición. Esta es
 * la única regla derivada de la composición y la comparten dibujo, error y
 * gate; fuera de Rail no agrega ningún requisito.
 */
export function compositionRequiresBirthDate(
  passenger: { birthDateRequired?: true } | undefined,
): boolean {
  return passenger?.birthDateRequired === true;
}

export interface DeclaredPassengerFields {
  /** Se piden en TODAS las filas (incluida la del titular). */
  all: DeclaredPassengerField[];
  /** Se piden SOLO en la fila del titular (contacto / conductor). */
  contact: DeclaredPassengerField[];
}

/** Todo lo declarado, sin importar el alcance. */
function declaredEntries(
  declared: DeclaredPassengerFields,
): DeclaredPassengerField[] {
  return [...declared.all, ...declared.contact];
}

/**
 * ¿La oferta declara algo que el proveedor valida recién al CONFIRMAR? Se
 * deriva de los campos, no de un flag paralelo: el aviso pre-hold no puede
 * decir una cosa distinta de la que dicen los badges de la grilla.
 *
 * Se piden IGUAL antes del hold aunque no lo bloqueen: el hold es el único
 * momento en que los datos del pasajero viajan al hub (el confirm solo lleva
 * respuestas a `pendingQuestions`), así que dejarlos vacíos deja un confirm
 * imposible de completar (21.23.2). Por eso el aviso.
 */
export function confirmValidatedFields(
  declared: DeclaredPassengerFields,
): RuteroDeclarablePassengerField[] {
  return declaredEntries(declared)
    .filter((entry) => entry.level === 'confirm')
    .map((entry) => entry.field);
}

/** Lo que necesita esta resolución de una oferta. */
export interface PassengerFieldsSource {
  requiredPassengerInfo?: unknown;
  recommendedPassengerInfo?: unknown;
  /**
   * Circuitos: el proveedor rechaza la reserva sin pasaporte. Es la MISMA
   * exigencia que `requiredPassengerInfo.all`, publicada por otra vía (la ficha
   * del producto), así que se absorbe acá y aguas abajo hay un solo mecanismo.
   */
  passportRequired?: boolean;
}

const EMPTY: DeclaredPassengerFields = {
  all: [],
  contact: [],
};

export interface QuoteScopedMissingPassengerFields {
  quoteAuthority: string;
  refs: RuteroPassengerFieldRef[];
}

function accumulate(
  target: Map<RuteroDeclarablePassengerField, PassengerFieldLevel>,
  fields: RuteroDeclarablePassengerField[] | undefined,
  level: PassengerFieldLevel,
): void {
  for (const field of fields ?? []) {
    target.set(field, strongest(target.get(field), level));
  }
}

/** En el orden canónico del vocabulario, no en el que los mandó el proveedor. */
function ordered(
  fields: Map<RuteroDeclarablePassengerField, PassengerFieldLevel>,
): DeclaredPassengerField[] {
  return RUTERO_DECLARABLE_PASSENGER_FIELDS.filter(
    (field) => fields.has(field) && PASSENGER_FIELD_INPUT[field] !== 'none',
  ).map((field) => ({
    field,
    level: fields.get(field) as PassengerFieldLevel,
  }));
}

/**
 * Una sola normalización para la autoridad proactiva y la aprendida al
 * rechazar un hold. Evita que precedencia, deduplicación entre alcances u orden
 * canónico diverjan según de dónde salió la exigencia.
 */
function normalizedPassengerFields(
  all: Map<RuteroDeclarablePassengerField, PassengerFieldLevel>,
  contact: Map<RuteroDeclarablePassengerField, PassengerFieldLevel>,
): DeclaredPassengerFields {
  // La fila del titular ya pinta todo lo de `all`: repetir ahí un campo sería
  // pedirlo dos veces. Solo sobrevive en `contact` lo que agrega exigencia —
  // un nivel MÁS fuerte del que ya se le pide a cada pasajero.
  for (const [field, contactLevel] of [...contact]) {
    const inAll = all.get(field);
    if (inAll && LEVEL_RANK[inAll] >= LEVEL_RANK[contactLevel]) {
      contact.delete(field);
    }
  }

  return {
    all: ordered(all),
    contact: ordered(contact),
  };
}

/**
 * Los campos a pedir, por alcance. Tolera los dos shapes del contrato (la lista
 * plana vieja y el objeto nuevo) y cualquier basura degrada a "no pido nada
 * extra": una oferta con un shape inesperado tiene que poder reservarse, no
 * romper el modal.
 */
export function declaredPassengerFields(
  offer: PassengerFieldsSource | null | undefined,
): DeclaredPassengerFields {
  if (!offer) return EMPTY;
  const required = sanitizeRuteroRequiredPassengerInfo(
    offer.requiredPassengerInfo,
  );
  const recommended = sanitizeRuteroRecommendedPassengerInfo(
    offer.recommendedPassengerInfo,
  );

  // `validatedAt` es del bloque requerido entero: dice cuándo valida el
  // proveedor TODO lo que exige en esta oferta. Ausente o desconocido ya lo
  // normalizó el sanitizador a `hold`, que es el default del contrato.
  const requiredLevel: PassengerFieldLevel =
    required?.validatedAt === 'confirm' ? 'confirm' : 'hold';

  const all = new Map<RuteroDeclarablePassengerField, PassengerFieldLevel>();
  const contact = new Map<RuteroDeclarablePassengerField, PassengerFieldLevel>();
  accumulate(all, required?.all, requiredLevel);
  accumulate(contact, required?.contact, requiredLevel);
  accumulate(all, recommended?.all, 'recommended');
  accumulate(contact, recommended?.contact, 'recommended');
  // El proveedor de circuitos rechaza la reserva sin pasaporte: es exigencia
  // del hold aunque se publique por la ficha del producto.
  if (offer.passportRequired === true) {
    all.set('passportNumber', strongest(all.get('passportNumber'), 'hold'));
  }

  return normalizedPassengerFields(all, contact);
}

/**
 * Promueve los refs sanitizados que Rutero aprendió en el hold a autoridad
 * requerida para ESA cotización. No muta la declaración de la oferta ni
 * cambia su metadata `validatedAt`.
 */
export function withMissingPassengerFields(
  declared: DeclaredPassengerFields,
  refs: readonly unknown[],
): DeclaredPassengerFields {
  const all = new Map(
    declared.all.map((entry) => [entry.field, entry.level] as const),
  );
  const contact = new Map(
    declared.contact.map((entry) => [entry.field, entry.level] as const),
  );

  for (const ref of sanitizeRuteroMissingPassengerFields(refs)) {
    const parsed = parseRuteroPassengerFieldRef(ref);
    if (!parsed) continue;
    // Nivel `hold`, sea cual sea el que declaraba la oferta: el proveedor
    // acaba de RECHAZAR el hold por ese campo, así que en esta cotización
    // bloquea reservar aunque su declaración dijera que validaba al confirmar.
    (parsed.scope === 'all' ? all : contact).set(parsed.field, 'hold');
  }

  return normalizedPassengerFields(all, contact);
}

/**
 * Una sesión de Tours puede pedir fechas de nacimiento solo para calcular
 * edades a la fecha del servicio. Se incorporan al mismo formulario de
 * pasajeros, sin crear un segundo bloque ni convertirlas en una exigencia del
 * proveedor para el hold.
 */
/**
 * La forma del PEDIDO comiteado, en lo único que condiciona qué datos tienen
 * sentido pedir. No es el vertical: es lo que ESTA búsqueda pidió.
 *
 * Cada propiedad es tri-estado a propósito. `undefined` significa "este pedido
 * no declara esa forma", y NO se trata como un no: un vertical que no publica
 * si hay tramo de vuelta no puede borrarle un campo a la oferta. Solo se
 * descarta lo que el pedido dice explícitamente que no tiene (21.18.4).
 */
export interface RequestShape {
  /** ¿El pedido tiene tramo de vuelta? `undefined` = el vertical no lo declara. */
  hasReturnLeg?: boolean;
}

/**
 * Qué campos tienen sentido para una forma de pedido. Exhaustivo a propósito:
 * un campo nuevo del vocabulario no compila hasta que se decide si algún
 * pedido puede no tenerlo.
 */
const FIELD_BELONGS_TO_SHAPE: Record<
  RuteroDeclarablePassengerField,
  (shape: RequestShape) => boolean
> = {
  type: () => true,
  title: () => true,
  birthDate: () => true,
  email: () => true,
  phone: () => true,
  nationality: () => true,
  countryOfResidence: () => true,
  passportNumber: () => true,
  passportExpiry: () => true,
  flightNumber: () => true,
  // Un solo ida no tiene vuelo de regreso. Se descarta únicamente cuando el
  // pedido lo dice: sin tramo de vuelta declarado, el campo se respeta.
  returnFlightNumber: (shape) => shape.hasReturnLeg !== false,
  driverNotes: () => true,
};

export interface DeclaredFieldsForShape {
  /** Lo que se dibuja Y se valida. Una sola lista para las dos cosas. */
  declared: DeclaredPassengerFields;
  /**
   * Lo que el conector exige para decidir (`hold` o `confirm`) y este pedido
   * no tiene. No es un campo que le falte al asesor —no hay dónde escribirlo,
   * ni sentido en hacerlo—: es una contradicción del conector.
   *
   * Conserva su NIVEL porque no alcanza con decirla: un requerido para
   * reservar que el pedido no puede tener hace que el hub rechace SIEMPRE, así
   * que además de explicarse tiene que BLOQUEAR — si no, el asesor manda un
   * pedido que se sabe imposible, y el rechazo promueve el mismo campo que se
   * vuelve a descartar, sin salida (21.18.1). Lo recomendado se descarta en
   * silencio: no condiciona ninguna decisión.
   */
  contradictions: {
    ref: RuteroPassengerFieldRef;
    level: PassengerFieldLevel;
  }[];
}

/**
 * Filtra la declaración por la forma del pedido. Es el ÚNICO lugar donde se
 * decide: aguas abajo, dibujar y validar consumen esta misma lista, así que no
 * puede quedar un campo validado sin control donde escribirlo ni un control
 * pidiendo algo que este viaje no tiene (21.9).
 */
export function declaredFieldsForRequestShape(
  declared: DeclaredPassengerFields,
  shape: RequestShape,
): DeclaredFieldsForShape {
  const contradictions: DeclaredFieldsForShape['contradictions'] = [];

  const keep = (
    scope: RuteroPassengerInfoScope,
    entries: DeclaredPassengerField[],
  ) =>
    entries.filter((entry) => {
      if (FIELD_BELONGS_TO_SHAPE[entry.field](shape)) return true;
      if (entry.level !== 'recommended') {
        contradictions.push({
          ref: ruteroPassengerFieldRef(scope, entry.field),
          level: entry.level,
        });
      }
      return false;
    });

  return {
    declared: {
      all: keep('all', declared.all),
      contact: keep('contact', declared.contact),
    },
    contradictions,
  };
}

/** Los campos de una fila: el titular suma los suyos a los de todos. */
export function fieldsForRow(
  declared: DeclaredPassengerFields,
  isHolder: boolean,
): DeclaredPassengerField[] {
  if (!isHolder) return declared.all;
  const contactFields = new Set(declared.contact.map((entry) => entry.field));
  return [
    ...declared.all.filter((entry) => !contactFields.has(entry.field)),
    ...declared.contact,
  ].sort(
    (a, b) =>
      RUTERO_DECLARABLE_PASSENGER_FIELDS.indexOf(a.field) -
      RUTERO_DECLARABLE_PASSENGER_FIELDS.indexOf(b.field),
  );
}

/**
 * Los campos que dibuja la grilla GENÉRICA de una fila.
 *
 * `holderFieldsRenderedApart` son los que ya tienen control propio **en la fila
 * del titular** (los vuelos de transfers, que viven arriba junto a los horarios
 * de cada tramo). Suprimirlos por nombre en TODAS las filas dejaba a los
 * acompañantes validados contra un campo sin ningún control donde escribirlo:
 * con `all.flightNumber` el botón de reservar no se habilitaba nunca.
 *
 * La supresión es por campo Y alcance: lo que el bloque especial cubre para el
 * titular, los acompañantes lo siguen necesitando en la grilla.
 */
export function fieldsForGenericRow(
  declared: DeclaredPassengerFields,
  isHolder: boolean,
  holderFieldsRenderedApart: ReadonlySet<RuteroDeclarablePassengerField>,
): DeclaredPassengerField[] {
  return fieldsForRow(declared, isHolder).filter(
    (entry) => !(isHolder && holderFieldsRenderedApart.has(entry.field)),
  );
}

/**
 * Los campos que dibuja el FORMULARIO, repartidos entre sus dos bloques.
 *
 * `row` va en cada fila de pasajero —titular incluido— y `contact` en el
 * bloque propio del titular, que existe solo si tiene algo. `renderedApart`
 * son los que ya tienen control propio en otro lado (los vuelos del titular,
 * que viven junto al horario de su tramo).
 *
 * Es UNA función y no dos listas sueltas por un bug concreto: al separar el
 * bloque de contacto, la supresión se aplicó a la fila y no al bloque, y el
 * vuelo del titular terminó dibujado dos veces —una en Horarios y otra en sus
 * datos de contacto—. Mientras las dos listas salgan de acá, un campo no puede
 * aparecer en los dos lugares ni escaparse de la supresión (#2374).
 *
 * Ojo: esto reparte el DIBUJO. La validación sigue mirando `fieldsForRow`, que
 * al titular le suma sus campos de contacto: que un dato se cargue en otro
 * bloque no lo hace opcional.
 */
export function passengerFormFields(
  declared: DeclaredPassengerFields,
  renderedApart: ReadonlySet<RuteroDeclarablePassengerField> = new Set(),
): { row: DeclaredPassengerField[]; contact: DeclaredPassengerField[] } {
  return {
    row: declared.all,
    contact: declared.contact.filter((entry) => !renderedApart.has(entry.field)),
  };
}

/**
 * ¿El campo está COMPLETO? No alcanza con "no vacío" para todos: un teléfono a
 * medio tipear es un string con contenido pero un rechazo seguro del proveedor,
 * y para entonces la reserva ya existe. Punto único: lo usan la validación del
 * hold, el marcado de filas y los refs que reporta el servidor.
 */
export function hasPassengerFieldValue(
  pax: RuteroPassengerPayload | undefined,
  field: RuteroDeclarablePassengerField,
): boolean {
  const value = pax?.[field];
  if (typeof value !== 'string' || !value.trim()) return false;
  if (PASSENGER_FIELD_INPUT[field] === 'phone') return isValidE164(value);
  return true;
}

/**
 * ¿El valor cargado EXCEDE el tope del campo? Solo puede pasar en el
 * multilínea: los demás controles recortan en el input (`maxLength`). Acá no
 * se recorta a propósito — el contrato del hub responde 400 en vez de truncar
 * porque cortar una instrucción al conductor por la mitad puede cambiarle el
 * sentido, y un recorte de browser en un paste haría exactamente eso, en
 * silencio. Punto único: lo usan el control (para pintar el error de campo) y
 * la validación del hold (para bloquear el CTA) — la misma expresión dibuja y
 * decide (21.9).
 */
export function passengerFieldValueTooLong(
  value: string | undefined,
  field: RuteroDeclarablePassengerField,
): boolean {
  const max = PASSENGER_FIELD_MAX_LENGTH[field];
  if (!max || typeof value !== 'string') return false;
  return value.length > max;
}

/**
 * Los campos cuyo valor excede su tope, como refs del contrato y sobre TODO el
 * vocabulario (no solo lo declarado): el payload manda lo que tenga valor, así
 * que un excedente viaja igual y es un 400 seguro del borde. `contact.x`
 * cuando el exceso está solo en la fila del titular; `all.x` si lo tiene un
 * acompañante.
 */
export function passengerFieldsOverLimit(
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerFieldRef[] {
  const refs: RuteroPassengerFieldRef[] = [];
  const contactIndex = bookingContactPassengerIndex(passengers);
  for (const field of RUTERO_DECLARABLE_PASSENGER_FIELDS) {
    const companionOver = passengers
      .some((pax, index) =>
        index !== contactIndex && passengerFieldValueTooLong(pax[field], field),);
    if (companionOver) {
      refs.push(ruteroPassengerFieldRef('all', field));
    } else if (passengerFieldValueTooLong(
      bookingContactPassenger(passengers)?.[field],
      field,
    )) {
      refs.push(ruteroPassengerFieldRef('contact', field));
    }
  }
  return refs;
}

/**
 * De la autoridad reportada conserva como error solo lo que los valores
 * actuales todavía no cumplen. `contact.x` mira al titular; `all.x`, a cada
 * pasajero (no alcanza con que uno solo tenga valor).
 */
export function unresolvedMissingPassengerFields(
  refs: readonly unknown[],
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerFieldRef[] {
  return sanitizeRuteroMissingPassengerFields(refs).filter((ref) => {
    const parsed = parseRuteroPassengerFieldRef(ref);
    if (!parsed) return false;
    if (parsed.scope === 'contact') {
      return !hasPassengerFieldValue(
        bookingContactPassenger(passengers),
        parsed.field,
      );
    }
    return (
      passengers.length === 0 ||
      passengers.some((pax) => !hasPassengerFieldValue(pax, parsed.field))
    );
  });
}

/**
 * Une rechazos repetidos de la misma cotización. Una autoridad distinta
 * reemplaza la anterior para que los requisitos aprendidos nunca crucen de
 * una provenance a otra.
 */
export function mergeQuoteScopedMissingPassengerFields(
  current: QuoteScopedMissingPassengerFields | null,
  quoteAuthority: string,
  refs: readonly unknown[],
): QuoteScopedMissingPassengerFields {
  return {
    quoteAuthority,
    refs: sanitizeRuteroMissingPassengerFields([
      ...(current?.quoteAuthority === quoteAuthority ? current.refs : []),
      ...refs,
    ]),
  };
}

export function missingPassengerFieldsForQuote(
  current: QuoteScopedMissingPassengerFields | null,
  quoteAuthority: string | null | undefined,
): RuteroPassengerFieldRef[] {
  return current && current.quoteAuthority === quoteAuthority
    ? current.refs
    : [];
}

/**
 * Lo que falta en ESTA fila para UN nivel. El nivel es un parámetro y no un
 * El nivel se conserva aunque Bandido adelante la precondición: sigue siendo
 * necesario para explicar cuándo valida el proveedor y para defender reservas
 * históricas que ya llegaron al hold.
 */
export function rowMissingFields(
  declared: DeclaredPassengerFields,
  pax: RuteroPassengerPayload | undefined,
  isHolder: boolean,
  level: PassengerFieldLevel,
): RuteroDeclarablePassengerField[] {
  return fieldsForRow(declared, isHolder)
    .filter(
      (entry) =>
        entry.level === level && !hasPassengerFieldValue(pax, entry.field),
    )
    .map((entry) => entry.field);
}

/**
 * Datos que tienen que viajar ANTES de crear el hold. Incluye tanto `hold`
 * como `confirm`: el proveedor puede validar los segundos más tarde, pero el
 * contrato de confirmación no vuelve a transportar pasajeros. Dejarlos vacíos
 * crearía una reserva que la propia UI ya sabe que no puede completar.
 */
export function missingPassengerFieldsBeforeHold(
  declared: DeclaredPassengerFields,
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerFieldRef[] {
  const refs: RuteroPassengerFieldRef[] = [];
  const requiredBeforeHold = (level: PassengerFieldLevel) =>
    level === 'hold' || level === 'confirm';

  for (const entry of declared.all) {
    if (!requiredBeforeHold(entry.level)) continue;
    if (
      passengers.length === 0 ||
      passengers.some((pax) => !hasPassengerFieldValue(pax, entry.field))
    ) {
      refs.push(ruteroPassengerFieldRef('all', entry.field));
    }
  }

  for (const entry of declared.contact) {
    if (!requiredBeforeHold(entry.level)) continue;
    if (
      !hasPassengerFieldValue(
        bookingContactPassenger(passengers),
        entry.field,
      )
    ) {
      refs.push(ruteroPassengerFieldRef('contact', entry.field));
    }
  }

  return refs;
}

/** ¿Se puede reservar sin crear un hold imposible de confirmar? */
export function passengersSatisfyDeclaration(
  declared: DeclaredPassengerFields,
  passengers: RuteroPassengerPayload[],
): boolean {
  return (
    passengers.every(
      (pax) => pax.firstName.trim() && pax.lastName.trim(),
    ) && missingPassengerFieldsBeforeHold(declared, passengers).length === 0
  );
}

/**
 * Lo que falta de lo que el proveedor valida AL CONFIRMAR, como refs del
 * contrato (`contact.x` / `all.x`) y en el orden canónico del vocabulario.
 *
 * Es UNA expresión: de acá salen la lista de la sección "Falta para confirmar"
 * y los motivos que deshabilitan el CTA de Confirmar. Si fueran dos, podría
 * quedar un botón bloqueado por un campo que la pantalla no nombra.
 *
 * Los pasajeros son los del REGISTRO autoritativo (los que viajaron al
 * proveedor en el hold), no los del formulario: después de reservar el
 * formulario ya no existe.
 */
export function pendingConfirmPassengerFields(
  declared: DeclaredPassengerFields,
  passengers: readonly RuteroPassengerPayload[],
): RuteroPassengerFieldRef[] {
  const refs: RuteroPassengerFieldRef[] = [];

  for (const entry of declared.all) {
    if (entry.level !== 'confirm') continue;
    // Sin pasajeros no hay nada que dé el dato por cargado: se reporta
    // faltante, igual que la autoridad reportada por el hub.
    if (
      passengers.length === 0 ||
      passengers.some((pax) => !hasPassengerFieldValue(pax, entry.field))
    ) {
      refs.push(ruteroPassengerFieldRef('all', entry.field));
    }
  }

  for (const entry of declared.contact) {
    if (entry.level !== 'confirm') continue;
    if (!hasPassengerFieldValue(
      bookingContactPassenger(passengers),
      entry.field,
    )) {
      refs.push(ruteroPassengerFieldRef('contact', entry.field));
    }
  }

  return refs;
}

/**
 * Un campo señalado por el hub como faltante (`contact.phone`) aplica a la fila
 * del titular; `all.x`, a todas. Se compara con los refs tal cual: el mensaje
 * del operador no se parsea nunca.
 */
export function isPassengerFieldUnresolved(
  unresolved: readonly RuteroPassengerFieldRef[],
  field: RuteroDeclarablePassengerField,
  isHolder: boolean,
): boolean {
  if (unresolved.includes(ruteroPassengerFieldRef('all', field))) return true;
  return (
    isHolder && unresolved.includes(ruteroPassengerFieldRef('contact', field))
  );
}

/** Los alcances de un ref, para volver a pintar el aviso en la fila correcta. */
export function missingPassengerFieldScopes(
  refs: readonly RuteroPassengerFieldRef[],
): { scope: RuteroPassengerInfoScope; field: RuteroDeclarablePassengerField }[] {
  return refs
    .map((ref) => parseRuteroPassengerFieldRef(ref))
    .filter(
      (
        parsed,
      ): parsed is {
        scope: RuteroPassengerInfoScope;
        field: RuteroDeclarablePassengerField;
      } => parsed !== null,
    );
}
